import React, { JSX, useEffect, useState, useRef } from "react";
import {
  BaseLogo,
  ImageLogo,
  LogoEditStatus,
  isChangeColor,
  isDigital,
} from "../../../../core/models/entities/BaseLogo";
import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCircleExclamation,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import Tooltip from "@mui/material/Tooltip";

import { PrintMethodAutoComplete } from "./PrintMethodAutoComplete";
import { ImageLogoForm } from "../models/ImageLogoForm";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import {
  Color,
  LogoOptions,
  TransferMethod,
} from "../../../../core/models/entities/LogoOptions";
import LoadingElement from "../../../components/loading-element/loading-element";
import { ColorChange } from "../../logo-list/hooks/useLogoActionsHandler";
import { LogoImage } from "../../../components/Logo/LogoImage";
import { useConfirmationDialog } from "../../../components/Dialogs/confirmation-dialog";
import DigitalColorBlock from "./DigitalColorBlock";
import LogoColorChanger from "./LogoColorChanger";
import { CustomAutoComplete } from "../../../components/Input/CustomAutoComplete";
import { VendorSearchAutoComplete } from "../../../components/Input/VendorSearchAutoComplete";
import { Organization } from "../../../../core/models/entities/Organization";
import { Role } from "../../../../core/models/entities/Role";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { LogoResize } from "./LogoResize";
import { faSave } from "@fortawesome/free-regular-svg-icons";
import { LogoRuler } from "./LogoRuler";
import { excludeScreenPrintMethods } from "../../../../utils/functions/TransferMethodUtil";

export interface LogoEditorTool_VM {
  options?: LogoOptions;
  logo: BaseLogo;
  onCancel: () => void;
  onDraft?: () => void;
  onCommit: () => void;
  onUpdateColor: (colors: ColorChange[]) => void;
  tempColorImage: string | null;
  isLoading: boolean;
  showCustomerInput: boolean;
  customers: Organization[];
  logoForm: ImageLogoForm;
  updateForm: (form: ImageLogoForm) => void;
  onResize: () => void;
  isInDesignEditor?: boolean;
}

function LogoEditorTool({
  options,
  logo: baseLogo,
  onDraft,
  onCommit,
  onCancel,
  onUpdateColor,
  isLoading,
  showCustomerInput,
  customers,
  logoForm,
  updateForm,
  onResize,
  isInDesignEditor = false,
}: LogoEditorTool_VM): JSX.Element {
  // Queue the logo for saving as draft.
  // Some bug causes useEffect to run twice, which results in an infinite state-update loop.
  // Not fixing this bug now, so adding a state-trigger hack instead.
  const [queued, setQueued] = useState(false);

  useEffect(() => {
    if (queued) {
      onDraft?.call(null);
      setQueued(false);
    }
    // TODO Should be using `logoForm.transferMethod` as dependency instead,
    //  but this causes the effect to run twice and loop indefinitely.
  }, [queued]);

  const { role } = useProfileContext();
  const logo = baseLogo as ImageLogo;

  const containerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);

  function handleCommit() {
    confirmationDialog.open();
  }

  const confirmationDialog = useConfirmationDialog(
    "Are you sure you want to save and commit this logo?",
    onCommit,
  );

  const digitalTransferDialog = useConfirmationDialog(
    "Switching to a digital transfer method will remove the ability to edit colors, as digital transfers use CMYK colors instead of Pantone.\n\nThe colors will also be reverted to the original file colors.\n\nAre you sure you want to proceed?",
    () => {
      if (pendingMethod) {
        applyMethodChange(pendingMethod);
        setPendingMethod(null);
      }
    },
  );

  const [pendingMethod, setPendingMethod] = useState<TransferMethod | null>(
    null,
  );

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    updateForm({
      ...logoForm,
      [name]: value,
    });
  }

  function handleSizeChange(newWidth: number, newHeight: number) {
    updateForm({
      ...logoForm,
      widthMm: newWidth,
      heightMm: newHeight,
    });
  }

  function onUpdateCustomer(customer?: Organization) {
    updateForm({
      ...logoForm,
      customer: customer ?? logoForm.customer,
    });
  }

  function updateColorsHandler(colors: ColorChange[]) {
    onUpdateColor(colors);
  }

  function applyMethodChange(method: TransferMethod | null) {
    if (method) {
      const [maxWidth, maxHeight] = method.size.split("x").map(Number);
      updateForm({
        ...logoForm,
        transferMethod: method,
        heightMm: logoForm.heightMm > maxHeight ? maxHeight : logoForm.heightMm,
        widthMm: logoForm.widthMm > maxWidth ? maxWidth : logoForm.widthMm,
      });
    } else {
      updateForm({
        ...logoForm,
        transferMethod: method,
      });
    }
    // Mark the logo as queued for saving as draft.
    setQueued(true);
  }

  function changePrintMethod(method: TransferMethod | null) {
    const currentIsDigital =
      logoForm.transferMethod?.typeAndKey.toLowerCase().includes("digital") ||
      false;
    const newIsDigital =
      method?.typeAndKey.toLowerCase().includes("digital") || false;

    if (!currentIsDigital && newIsDigital) {
      setPendingMethod(method);
      digitalTransferDialog.open();
      return;
    }

    applyMethodChange(method);
  }

  if (!options) {
    return <></>;
  }

  // Screen-print methods must not be selectable in the frontend (NAV-import only);
  // the currently selected method is kept so existing/cloned logos still display it.
  const filteredMethods = excludeScreenPrintMethods(
    options.transferMethods,
    logoForm.transferMethod?.key
  ).filter((x) => {
    // Exclude digital methods for non-digital logos
    if (isDigital(logo)) {
      return (x.method1 + x.method2 + x.method3)
        .toLowerCase()
        .includes("digital");
    }

    // Exclude "direk tryk" methods if logo has more than 5 colors
    if (logo.colors.length > 5 && x.type.toLowerCase() === "direk tryk") {
      return false;
    }

    return true;
  });

  const isColorStop2 =
    logoForm.transferMethod?.key === "COLORSTOP2" ||
    logoForm.transferMethod?.key === "COLORSTOP";

  const currentColors =
    logo.colorDetails && logo.colorDetails.length > 0
      ? logo.colorDetails
      : options.allColors.filter((c) => logo.colors.includes(c.id));

  const hasMetallicOrNeon = currentColors.some((c) => {
    const colorInList = options.allColors.find((ac) => ac.id === c.id);
    return colorInList?.isMetallic || colorInList?.isNeon;
  });

  const showColorStopWarning = isColorStop2 && hasMetallicOrNeon;

  return (
    <>
      {confirmationDialog.component}
      {digitalTransferDialog.component}
      {isLoading && <LoadingElement transparent />}
      <div className={"h-full flex flex-col"}>
        <div
          id={"toolbar"}
          className={"bg-gray-100 space-y-2 py-2 px-3 rounded-tr-md"}
        >
          <div className={"flex items-center space-x-3"}>
            <h2 className={"flex-1 font-bold text-gray-600"}>EDITING LOGO</h2>
            <SecondaryButton
              width={"sm"}
              disabled={isLoading}
              onClick={onCancel}
            >
              Cancel
            </SecondaryButton>
            {logo.status === LogoEditStatus.Draft && !!onDraft && (
              <PrimaryButton
                width={"sm"}
                className={"px-4"}
                disabled={isLoading}
                onClick={onDraft}
              >
                <FontAwesomeIcon icon={faSave} />
                <span className={"flex-1"}>Save Draft</span>
              </PrimaryButton>
            )}
            <PrimaryButton
              width={"md"}
              className={"px-4"}
              disabled={isLoading}
              onClick={handleCommit}
            >
              <FontAwesomeIcon icon={faCheck} />
              <span className={"flex-1"}>Save Changes & Approve</span>
            </PrimaryButton>
          </div>
          <div className={"text-gray-600 flex items-center space-x-3 text-xs"}>
            {showCustomerInput && role === Role.Vendor && (
              <div className={"flex items-center space-x-2"}>
                <label className={"uppercase"}>Customer</label>
                <CustomAutoComplete<Organization>
                  options={customers}
                  getOptionKey={(org) => org.id}
                  displayOption={(org) => org.name}
                  key={logoForm.customer?.id}
                  onChange={(org) => onUpdateCustomer(org!)}
                  value={logoForm.customer ?? null}
                  placeholder="Search for customer..."
                />
              </div>
            )}
            {role === Role.Admin && (
              <div className={"flex items-center space-x-2"}>
                <label className={"uppercase"}>Vendor</label>
                <VendorSearchAutoComplete
                  value={logoForm.customer}
                  onChange={(org) => onUpdateCustomer(org!)}
                  placeholder="Search for vendor..."
                />
              </div>
            )}

            <div className={"flex items-center space-x-2"}>
              <label className={"uppercase"}>Title</label>
              <input
                className={"input h-9"}
                disabled={isLoading}
                name={nameOf<ImageLogoForm>((lf) => lf.title)}
                type="text"
                value={logoForm.title}
                onChange={handleInputChange}
                maxLength={30}
              />
            </div>
            {isDigital(logo) && (
              <div className={"flex items-center space-x-2"}>
                <label className={"uppercase"}>Type</label>
                <button
                  className={"but-32 p-1 border rounded flex items-center"}
                >
                  <div className={"flex items-center h-5 w-5 mr-0.5"}>
                    <DigitalColorBlock />
                  </div>
                  Digital Transfer
                </button>
              </div>
            )}
            <div className={"flex items-center space-x-2 flex-1"}>
              <label className={"uppercase"}>Method</label>
              <PrintMethodAutoComplete
                disabled={isLoading}
                value={logoForm.transferMethod?.key ?? ""}
                methods={filteredMethods}
                onChange={changePrintMethod}
              />
              <Tooltip title={<PrintMethodInfo />} placement={"bottom-start"}>
                <FontAwesomeIcon size={"xl"} icon={faCircleInfo} />
              </Tooltip>
            </div>
            <LogoResize
              width={logoForm.widthMm}
              height={logoForm.heightMm}
              onSizeChange={handleSizeChange}
              onResize={onResize}
              disabled={isLoading}
              transferMethod={logoForm.transferMethod}
            />
          </div>
          {isChangeColor(logo) && (
            <LogoColorChanger
              disabled={isLoading}
              options={options}
              logo={logo}
              onUpdateColor={updateColorsHandler}
              isInDesignEditor={isInDesignEditor}
            />
          )}
        </div>
        <div
          ref={containerRef}
          // Outside the design editor the mm rulers render as absolute 24px strips at the
          // container's top/left. Reserve that space (pt-6/pl-6 = 24px = RULER_SIZE) and clip,
          // so the image can never sit underneath the opaque ruler (top looked cut off on clone).
          className={
            "flex-1 flex justify-center items-center relative" +
            (!isInDesignEditor ? " overflow-hidden pt-6 pl-6" : "")
          }
        >
          {!isInDesignEditor && (
            <LogoRuler
              containerRef={containerRef}
              logoRef={logoRef}
              widthMm={logo.widthMm}
              heightMm={logo.heightMm}
            />
          )}
          {isDigital(logo) && (
            <div className={"absolute top-0 left-0 p-2 z-20"}>
              <Tooltip placement={"right-start"} title={<IsDigitalInfo />}>
                <div>
                  <FontAwesomeIcon
                    className={"text-status-failed-1"}
                    size={"lg"}
                    icon={faCircleExclamation}
                  />
                </div>
              </Tooltip>
            </div>
          )}
          {showColorStopWarning && (
            <div className={"absolute top-0 left-0 p-2 z-20"}>
              <Tooltip
                placement={"right-start"}
                title={<ColorStopWarningInfo />}
              >
                <div>
                  <FontAwesomeIcon
                    className={"text-status-failed-1"}
                    size={"lg"}
                    icon={faCircleExclamation}
                  />
                </div>
              </Tooltip>
            </div>
          )}
          <LogoImage
            ref={logoRef}
            className={
              "min-h-[325px] min-w-[450px] max-h-[650px] max-w-[900px] object-scale-down bg-white rounded-bl"
            }
            logo={logo}
            useHighRes={true}
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      </div>
    </>
  );
}

export default LogoEditorTool;

function IsDigitalInfo() {
  return (
    <div className={"text-xs text-gray-100"}>
      <p>Logo can only be created in Digital Transfer.</p>
      <br />
      <p>This can be because of the following:</p>
      <ul>
        <li>• Logo isn’t a vector file.</li>
        <li>• Logo contains a color-gradient.</li>
        <li>• Logo has more than 10 colors.</li>
        <li>• Logo isn’t 100% opacity.</li>
      </ul>
      <br />
      <p>
        Also keep in mind that with digital transfers, it's not possible to
        produce metallic colors or neon colors.
      </p>
    </div>
  );
}

function PrintMethodInfo() {
  return (
    <div className={"text-xs text-gray-100 w-[160px] font-medium"}>
      <b>Standard</b> is the regular transfer for fabric.
      <br />
      <br />
      <b>Industrial</b> is the more hard-wearing transfer for fabric that is
      expected to be exposed to heavy wear.
      <br />
      <br />
      <b>Water Industrial</b> is the durable transfer for fabric where a softer,
      more breathable and more sustainable print is desired.
      <br />
      <br />
      <b>Water Reflex</b> provides safety and visibility from the surroundings
      in low-light and high-risk conditions.
      <br />
      <br />
      <b>Water Sport</b> is the transfer for highly stretchable fabrics such as
      running tights, team jerseys and other sports and training gear.
      <br />
      <br />
      <b>ColorStop</b> supports the fabric with an anti-migration layer that
      minimizes the risk of bleeding and discoloration.
      <br />
      <br />
      <b>Digital</b> is the ultimate option for precise reproduction of
      creative, detailed and color-rich designs.
      <br />
      <br />
      <b>Digital ColorStop</b> minimizes the risk of bleeding and discoloration
      for designs with lifelike images, gradients or a high number of colors.
      <br />
      <br />
      <b>Nylon</b> adheres firmly to the garment while the nylon outerwear
      battles the wet elements.
      <br />
      <br />
    </div>
  );
}

function ColorStopWarningInfo() {
  return (
    <div className={"text-xs text-gray-100"}>
      <p>
        With this transfer method it's not possible for the metallic colors or
        neon colors to have a metallic or neon effect.
      </p>
      <br />
      <p>
        It'll be produced as a normal color would, without the effect you'd
        expect when choosing this color on other transfer methods.
      </p>
    </div>
  );
}
